import {
  CheckmarkIcon,
  ChevronDownIcon,
  ChevronUpIcon,
} from '@otter-ai/den-icons';
import * as SelectPrimitive from '@radix-ui/react-select';
import { cx } from 'cva';
import { type ElementRef, forwardRef, type ReactNode } from 'react';
import {
  MenuList,
  MenuListIconIndicator,
  MenuListItem,
  MenuListLabel,
  MenuListSeparator,
} from '../MenuList';
import { usePortalContext } from '../Portal';

export type SelectRootProps = SelectPrimitive.SelectProps;
export const SelectRoot = SelectPrimitive.Root;

export type SelectTriggerProps = SelectPrimitive.SelectTriggerProps;
export const SelectTrigger = SelectPrimitive.Trigger;

export type SelectValueProps = SelectPrimitive.SelectValueProps;
export const SelectValue = SelectPrimitive.Value;

export type SelectPortalProps = SelectPrimitive.SelectPortalProps;

/**
 * Wrap the popup content in this to render in a React portal.
 *
 * If you do not see the popup content, ensure `<PortalProvider />` is wrapping your app.
 */
export const SelectPortal = (props: SelectPortalProps) => {
  return <SelectPrimitive.Portal {...usePortalContext()} {...props} />;
};

export type SelectContentProps = SelectPrimitive.SelectContentProps & {
  'data-testid'?: string;
};
export const SelectContent = forwardRef<
  ElementRef<typeof SelectPrimitive.Content>,
  SelectContentProps
>((props, forwardedRef) => {
  const {
    asChild,
    children,
    className,
    sideOffset = 8,
    'data-testid': dataTestId = 'select-content',
    ...rest
  } = props;
  return (
    <SelectPrimitive.Content
      ref={forwardedRef}
      asChild
      sideOffset={sideOffset}
      className={cx(
        'relative',
        'min-w-[min(var(--radix-popper-available-width),var(--radix-popper-anchor-width))]',
        'max-w-[var(--radix-popper-available-width)]',
        'max-h-[var(--radix-popper-available-height)]',
        'z-[2000]',
        className,
      )}
      data-testid={dataTestId}
      {...rest}
    >
      <MenuList asChild={asChild} elevated>
        {children}
      </MenuList>
    </SelectPrimitive.Content>
  );
});

export type SelectViewportProps = SelectPrimitive.SelectViewportProps;
export const SelectViewport = SelectPrimitive.Viewport;

export type SelectGroupProps = SelectPrimitive.SelectGroupProps;
export const SelectGroup = SelectPrimitive.Group;

export type SelectLabelProps = SelectPrimitive.SelectLabelProps & {
  'data-testid'?: string;
};

export const SelectLabel = forwardRef<
  ElementRef<typeof SelectPrimitive.Label>,
  SelectLabelProps
>((props, forwardedRef) => {
  const {
    asChild,
    children,
    'data-testid': dataTestId = 'select-label',
    ...rest
  } = props;
  return (
    <SelectPrimitive.Label
      ref={forwardedRef}
      asChild
      data-testid={dataTestId}
      {...rest}
    >
      <MenuListLabel asChild={asChild}>{children}</MenuListLabel>
    </SelectPrimitive.Label>
  );
});

export interface SelectItemProps extends SelectPrimitive.SelectItemProps {
  appendStart?: ReactNode;
  appendEnd?: ReactNode;
  'data-testid'?: string;
}

export const SelectItem = forwardRef<
  ElementRef<typeof SelectPrimitive.Item>,
  SelectItemProps
>((props, forwardedRef) => {
  const {
    className,
    appendStart,
    appendEnd = (
      <SelectPrimitive.ItemIndicator asChild>
        <MenuListIconIndicator>
          <CheckmarkIcon />
        </MenuListIconIndicator>
      </SelectPrimitive.ItemIndicator>
    ),
    asChild,
    children,
    'data-testid': dataTestId = 'select-item',
    ...rest
  } = props;

  return (
    <SelectPrimitive.Item
      ref={forwardedRef}
      asChild
      data-testid={dataTestId}
      {...rest}
    >
      <MenuListItem
        asChild={asChild}
        appendStart={appendStart}
        appendEnd={appendEnd}
      >
        <SelectPrimitive.ItemText>{children}</SelectPrimitive.ItemText>
      </MenuListItem>
    </SelectPrimitive.Item>
  );
});

export type SelectScrollUpButtonProps =
  SelectPrimitive.SelectScrollUpButtonProps & {
    'data-testid'?: string;
  };

export const SelectScrollUpButton = forwardRef<
  ElementRef<typeof SelectPrimitive.ScrollUpButton>,
  SelectScrollUpButtonProps
>((props, forwardedRef) => {
  const {
    className,
    'data-testid': dataTestId = 'select-scroll-up',
    ...rest
  } = props;
  return (
    <SelectPrimitive.ScrollUpButton
      ref={forwardedRef}
      className={cx(
        'flex',
        'justify-center',
        'absolute',
        'bg-default',
        'top-0',
        'left-0',
        'right-0',
        'z-[2]',
        className,
      )}
      data-testid={dataTestId}
      {...rest}
    >
      <ChevronUpIcon />
    </SelectPrimitive.ScrollUpButton>
  );
});

export type SelectScrollDownButtonProps =
  SelectPrimitive.SelectScrollDownButtonProps & {
    'data-testid'?: string;
  };

export const SelectScrollDownButton = forwardRef<
  ElementRef<typeof SelectPrimitive.ScrollDownButton>,
  SelectScrollDownButtonProps
>((props, forwardedRef) => {
  const {
    className,
    'data-testid': dataTestId = 'select-scroll-down',
    ...rest
  } = props;
  return (
    <SelectPrimitive.ScrollDownButton
      ref={forwardedRef}
      className={cx(
        'flex',
        'justify-center',
        'absolute',
        'bg-default',
        'bottom-0',
        'left-0',
        'right-0',
        'z-[2]',
        className,
      )}
      data-testid={dataTestId}
      {...rest}
    >
      <ChevronDownIcon />
    </SelectPrimitive.ScrollDownButton>
  );
});

export type SelectSeparatorProps = SelectPrimitive.SelectSeparatorProps & {
  'data-testid'?: string;
};

export const SelectSeparator = forwardRef<
  ElementRef<typeof SelectPrimitive.Separator>,
  SelectSeparatorProps
>((props, ref) => {
  const {
    asChild,
    children,
    'data-testid': dataTestId = 'select-separator',
    ...rest
  } = props;

  return (
    <SelectPrimitive.Separator
      ref={ref}
      asChild
      data-testid={dataTestId}
      {...rest}
    >
      <MenuListSeparator asChild={asChild}>{children}</MenuListSeparator>
    </SelectPrimitive.Separator>
  );
});
